import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Users, MessageCircle, TrendingUp, Medal, Share2, UserPlus, 
  Brain, HeartHandshake, Activity, Zap, Gift, Award, MapPin, 
  FileBadge, Calendar, Globe, BookOpen, Target, Lock, Plus, 
  PieChart as PieChartIcon, Trophy, Star, ArrowLeft
} from 'lucide-react';
import { 
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  LineChart, Line, AreaChart, Area, PieChart as ReChartPieChart, Pie, RadarChart,
  PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, Cell
} from 'recharts';

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.1
    }
  }
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: { type: 'spring', stiffness: 300, damping: 24 }
  }
};

// Advanced data structure for user analytics 
const socialConnectionData = [
  { month: 'Jan', connections: 12, messages: 45, personalGrowth: 25, socialCapital: 32 },
  { month: 'Feb', connections: 19, messages: 67, personalGrowth: 30, socialCapital: 40 },
  { month: 'Mar', connections: 15, messages: 52, personalGrowth: 28, socialCapital: 35 },
  { month: 'Apr', connections: 27, messages: 90, personalGrowth: 42, socialCapital: 55 },
  { month: 'May', connections: 32, messages: 115, personalGrowth: 48, socialCapital: 68 },
  { month: 'Jun', connections: 24, messages: 78, personalGrowth: 40, socialCapital: 60 },
];

const weeklyEngagementData = [
  { day: 'Mon', realWorldValue: 40, digitalValue: 25, xpGained: 65 },
  { day: 'Tue', realWorldValue: 65, digitalValue: 20, xpGained: 85 },
  { day: 'Wed', realWorldValue: 55, digitalValue: 30, xpGained: 85 },
  { day: 'Thu', realWorldValue: 85, digitalValue: 15, xpGained: 100 },
  { day: 'Fri', realWorldValue: 75, digitalValue: 22, xpGained: 97 },
  { day: 'Sat', realWorldValue: 35, digitalValue: 40, xpGained: 75 },
  { day: 'Sun', realWorldValue: 45, digitalValue: 35, xpGained: 80 },
];

const lifestyleBalanceData = [
  { name: 'Fiziksel Aktivite', value: 72, fullMark: 100 },
  { name: 'Sosyal Etkileşim', value: 86, fullMark: 100 },
  { name: 'Zihinsel Gelişim', value: 78, fullMark: 100 },
  { name: 'Sağlıklı Beslenme', value: 65, fullMark: 100 },
  { name: 'Uyku Kalitesi', value: 68, fullMark: 100 },
  { name: 'Stres Yönetimi', value: 80, fullMark: 100 },
];

const achievementCategories = [
  { name: 'Sosyal Becerileri', value: 32, color: '#3b82f6' },
  { name: 'Fiziksel Sağlık', value: 24, color: '#ef4444' },
  { name: 'Zihinsel Gelişim', value: 28, color: '#8b5cf6' },
  { name: 'Kişisel Hedefler', value: 18, color: '#10b981' },
  { name: 'Toplumsal Katkı', value: 14, color: '#f59e0b' },
];

const recentMilestones = [
  { id: 1, title: "Sosyal Kelebek", description: "5+ kişiyle gerçek etkileşim kuruldu", date: "2 gün önce", type: "social", xp: 150 },
  { id: 2, title: "Kitap Kurdu", description: "Bu ay 4. kitap tamamlandı", date: "5 gün önce", type: "knowledge", xp: 200 },
  { id: 3, title: "Erken Kalkan", description: "7 gün üst üste sabah 6'da kalkma", date: "1 hafta önce", type: "wellbeing", xp: 180 },
];

const habitStreaks = [
  { name: 'Egzersiz', current: 12, best: 18, atRisk: false, nextMilestone: 15 },
  { name: 'Meditasyon', current: 24, best: 30, atRisk: false, nextMilestone: 30 },
  { name: 'Erken Kalkma', current: 7, best: 14, atRisk: false, nextMilestone: 10 },
  { name: 'Su İçme', current: 0, best: 21, atRisk: true, nextMilestone: 7 },
  { name: 'Günlük Yazma', current: 4, best: 10, atRisk: false, nextMilestone: 5 },
];

// Enhanced stat card with gamification elements (level, progress, rewards)
const StatCard = ({ 
  title, 
  value, 
  icon: Icon, 
  description, 
  color, 
  isDarkMode,
  level = 1,
  progress = 70,
  onTrack = true,
  streak = null,
  reward = null,
  nextMilestone = null
}) => (
  <motion.div 
    className={`p-4 rounded-xl ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-lg relative overflow-hidden`}
    variants={itemVariants}
    whileHover={{ y: -5, transition: { duration: 0.2 } }}
  >
    {level > 1 && (
      <div className="absolute top-2 right-2 px-2 py-0.5 rounded-full bg-yellow-500/20 text-yellow-500 text-xs font-bold">
        LVL {level}
      </div>
    )}
    
    <div className="flex justify-between items-start">
      <div>
        <h3 className="text-sm opacity-70 mb-1">{title}</h3>
        <p className="text-2xl font-bold">{value}</p>
        {description && <p className="text-xs mt-1 opacity-70">{description}</p>}
        
        {streak && (
          <div className="mt-1.5 flex items-center">
            <Zap className="h-3 w-3 text-yellow-500 mr-1" />
            <span className="text-xs font-medium text-yellow-500">{streak} gün seri</span>
          </div>
        )}
        
        {nextMilestone && (
          <div className="mt-2 text-xs opacity-70">
            <span>Sonraki ödül: </span>
            <span className="font-medium">{nextMilestone}</span>
          </div>
        )}
      </div>
      
      <div className={`p-2 rounded-full bg-opacity-20`} style={{ backgroundColor: `${color}20` }}>
        <Icon className="h-5 w-5" style={{ color }} />
      </div>
    </div>
    
    {progress !== null && (
      <div className="mt-3">
        <div className="flex justify-between items-center text-xs mb-1">
          <span className="opacity-70">İlerleme</span>
          <span className="font-medium">{progress}%</span>
        </div>
        <div className={`w-full h-1.5 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'} rounded-full overflow-hidden`}>
          <motion.div
            className="h-full"
            style={{ backgroundColor: color }}
            initial={{ width: 0 }}
            animate={{ width: `${progress}%` }}
            transition={{ duration: 1, ease: "easeOut" }}
          />
        </div>
      </div>
    )}
    
    {reward && (
      <div className="mt-2 flex items-center">
        <Gift className="h-3.5 w-3.5 text-purple-500 mr-1.5" />
        <span className="text-xs font-medium text-purple-500">{reward}</span>
      </div>
    )}
    
    {!onTrack && (
      <div className="mt-2 flex items-center text-red-500">
        <Activity className="h-3.5 w-3.5 mr-1" />
        <span className="text-xs">Dikkat! Hedefin gerisinde kaldın</span>
      </div>
    )}
  </motion.div>
);

// Badge card for achievements and rewards
const BadgeCard = ({ title, icon: Icon, description, unlocked, progress, color, isDarkMode }) => (
  <motion.div 
    className={`relative p-4 rounded-xl ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-lg border ${unlocked ? `border-${color}-500/30` : 'border-gray-700/30'}`}
    variants={itemVariants}
    whileHover={{ scale: 1.02 }}
  >
    <div className="flex items-start">
      <div className={`p-3 rounded-lg mr-4 ${unlocked ? `bg-${color}-500/20` : 'bg-gray-700/20'}`}>
        <Icon className={`h-6 w-6 ${unlocked ? `text-${color}-500` : 'text-gray-500'}`} />
      </div>
      <div>
        <h3 className="font-semibold mb-1">{title}</h3>
        <p className="text-xs opacity-70 mb-3">{description}</p>
        
        {unlocked ? (
          <div className="flex items-center">
            <Award className="h-3.5 w-3.5 text-green-500 mr-1.5" />
            <span className="text-xs font-medium text-green-500">Kazanıldı</span>
          </div>
        ) : progress ? (
          <div className="w-full">
            <div className="flex justify-between items-center text-xs mb-1">
              <span className="opacity-70">İlerleme</span>
              <span className="font-medium">{progress}%</span>
            </div>
            <div className={`w-full h-1.5 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'} rounded-full overflow-hidden`}>
              <div 
                className="h-full bg-blue-500" 
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        ) : (
          <div className="flex items-center">
            <Lock className="h-3.5 w-3.5 text-gray-500 mr-1.5" />
            <span className="text-xs text-gray-500">Kilitli</span>
          </div>
        )}
      </div>
    </div>
  </motion.div>
);

const SocialStatsComponent = ({ isDarkMode = true, onBackToOverview }) => {
  const [activeTab, setActiveTab] = useState('personal');
  const [showAchievementDetails, setShowAchievementDetails] = useState(false);
  const [selectedAchievement, setSelectedAchievement] = useState(null);
  const [levelUpAnimation, setLevelUpAnimation] = useState(false);
  
  // Simulate level up animation effect
  useEffect(() => {
    const timer = setTimeout(() => {
      setLevelUpAnimation(true);
      setTimeout(() => setLevelUpAnimation(false), 3000);
    }, 5000);
    
    return () => clearTimeout(timer);
  }, []);
  return (
    <div className="space-y-6 relative">
      {/* Back Button */}
      {onBackToOverview && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <button
            onClick={onBackToOverview}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all duration-200 ${
              isDarkMode 
                ? 'bg-gray-800 hover:bg-gray-700 text-gray-200' 
                : 'bg-gray-100 hover:bg-gray-200 text-gray-800'
            }`}
          >
            <ArrowLeft className="h-4 w-4" />
            <span>İstatistiklere Geri Dön</span>
          </button>
        </motion.div>
      )}
      
      {/* Tab Navigation */}
      <div className="flex space-x-1 p-1 bg-gray-700/30 rounded-lg">
        {[
          { id: 'personal', label: 'Kişisel Gelişim', icon: <Brain size={14} className="mr-1" /> },
          { id: 'social', label: 'Sosyal Etkileşim', icon: <Users size={14} className="mr-1" /> },
          { id: 'achievements', label: 'Başarımlar', icon: <Award size={14} className="mr-1" /> }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center text-xs px-3 py-1.5 rounded-md flex-1 justify-center transition ${
              activeTab === tab.id 
                ? (isDarkMode ? 'bg-blue-500 text-white' : 'bg-blue-500 text-white') 
                : (isDarkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-200')
            }`}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </div>
      
      {/* Level Up Animation */}
      <AnimatePresence>
        {levelUpAnimation && (
          <motion.div
            initial={{ opacity: 0, scale: 0.8, y: -20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.8, y: -20 }}
            className="absolute inset-0 flex items-center justify-center z-10"
          >
            <div className={`p-6 rounded-2xl ${isDarkMode ? 'bg-gray-800/95' : 'bg-white/95'} shadow-xl border border-yellow-500 flex flex-col items-center`}>
              <motion.div 
                animate={{ 
                  scale: [1, 1.2, 1], 
                  rotate: [0, 10, -10, 0],
                  y: [0, -10, 0]
                }}
                transition={{ duration: 1, times: [0, 0.5, 1] }}
              >
                <Award size={70} className="text-yellow-500 mb-3" />
              </motion.div>
              <h3 className="text-xl font-bold mb-1">Seviye Atladın!</h3>
              <p className="opacity-70 mb-3">Sosyal Usta: Seviye 12</p>
              <div className="flex space-x-2 text-sm">
                <span className="text-yellow-500 font-semibold">+500 XP</span>
                <span className="text-blue-500 font-semibold">+3 Yetenek Puanı</span>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Personal Development Tab */}
      {activeTab === 'personal' && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0 }}
          className="space-y-6"
        >
          {/* Level and Overview */}
          <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-lg`}>
            <div className="flex justify-between mb-3">
              <div>
                <span className="text-xs opacity-70">Yaşam Ustası</span>
                <h2 className="text-xl font-bold">Seviye 12</h2>
              </div>
              <div className={`px-3 py-1 h-fit rounded-full text-xs font-medium ${isDarkMode ? 'bg-yellow-500/20 text-yellow-400' : 'bg-yellow-100 text-yellow-700'}`}>
                8,540 XP
              </div>
            </div>
            
            <div className="mb-1 flex justify-between text-xs">
              <span className="opacity-70">8,540 XP</span>
              <span className="opacity-70">10,000 XP</span>
            </div>
            
            <div className="w-full h-2 bg-gray-700 rounded-full overflow-hidden mb-4">
              <motion.div
                className="h-full bg-yellow-500"
                initial={{ width: 0 }}
                animate={{ width: '85.4%' }}
                transition={{ duration: 1.5, ease: "easeOut" }}
              />
            </div>
            
            <div className="flex justify-between items-center text-xs">
              <div className="flex items-center">
                <Target className="h-3.5 w-3.5 text-blue-500 mr-1.5" />
                <span>Sonraki hedef: 1,460 XP kaldı</span>
              </div>
              <span className="font-medium text-green-500">+245 XP bu hafta</span>
            </div>
          </div>
          
          {/* Life Balance Stats */}
          <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-lg`}>
            <h3 className="text-lg font-bold mb-4 flex items-center">
              <Brain className="h-5 w-5 mr-2 text-indigo-500" />
              Yaşam Dengesi
            </h3>
            
            <div className="h-72 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <RadarChart outerRadius={90} data={lifestyleBalanceData}>
                  <PolarGrid stroke={isDarkMode ? "#374151" : "#e5e7eb"} />
                  <PolarAngleAxis 
                    dataKey="name"
                    tick={{ fill: isDarkMode ? '#d1d5db' : '#4b5563', fontSize: 12 }}
                  />
                  <PolarRadiusAxis 
                    angle={30}
                    domain={[0, 100]} 
                    tick={{ fill: isDarkMode ? '#d1d5db' : '#4b5563' }}
                  />
                  <Radar
                    name="Yaşam Dengesi"
                    dataKey="value"
                    stroke="#8884d8"
                    fill="#8884d8"
                    fillOpacity={0.5}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: isDarkMode ? '#1f2937' : '#ffffff',
                      borderColor: isDarkMode ? '#374151' : '#e5e7eb',
                    }}
                  />
                </RadarChart>
              </ResponsiveContainer>
            </div>
            
            <div className="mt-2 flex justify-center">
              <div className={`px-3 py-1.5 rounded-lg ${isDarkMode ? 'bg-indigo-500/20' : 'bg-indigo-100'} inline-block`}>
                <span className="text-sm font-medium text-indigo-500">Denge Puanı: 75/100</span>
              </div>
            </div>
          </div>
          
          {/* Habit Streaks */}
          <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-lg`}>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold flex items-center">
                <Activity className="h-5 w-5 mr-2 text-green-500" />
                Alışkanlık Serileri
              </h3>
              
              <span className={`text-xs ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>
                4/5 seriler aktif
              </span>
            </div>
            
            <div className="space-y-4">
              {habitStreaks.map((habit, index) => (
                <div key={index}>
                  <div className="flex justify-between items-center mb-1.5">
                    <div className="flex items-center">
                      <div className={`h-2 w-2 rounded-full ${habit.atRisk ? 'bg-red-500' : 'bg-green-500'} mr-2`} />
                      <span className="font-medium">{habit.name}</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <div className="flex items-center">
                        <Zap className="h-3.5 w-3.5 text-yellow-500 mr-1" />
                        <span className="text-sm font-medium">{habit.current}</span>
                      </div>
                      <span className="text-xs opacity-70">En İyi: {habit.best}</span>
                    </div>
                  </div>
                  
                  <div className="flex items-center">
                    <div className="flex-1 h-2 bg-gray-700 rounded-full overflow-hidden mr-2">
                      <motion.div
                        className={`h-full ${habit.atRisk ? 'bg-red-500' : 'bg-green-500'}`}
                        initial={{ width: 0 }}
                        animate={{ width: `${(habit.current/habit.nextMilestone) * 100}%` }}
                        transition={{ duration: 0.8 }}
                      />
                    </div>
                    <span className="text-xs opacity-70">{habit.nextMilestone - habit.current} gün kaldı</span>
                  </div>
                </div>
              ))}
            </div>
            
            <motion.button 
              className={`w-full mt-4 py-2 rounded-lg border ${isDarkMode ? 'border-blue-500/30 text-blue-400 hover:bg-blue-500/10' : 'border-blue-500 text-blue-500 hover:bg-blue-50'} flex items-center justify-center`}
              whileTap={{ scale: 0.97 }}
            >
              <Plus className="h-4 w-4 mr-1.5" />
              <span className="text-sm">Yeni Alışkanlık Ekle</span>
            </motion.button>
          </div>
          
          {/* Recent Activity */}
          <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-lg`}>
            <h3 className="text-lg font-bold mb-4 flex items-center">
              <Calendar className="h-5 w-5 mr-2 text-amber-500" />
              Son Aktiviteler
            </h3>
            
            <div className="space-y-3">
              {recentMilestones.map((milestone) => (
                <div 
                  key={milestone.id} 
                  className={`p-3 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}
                >
                  <div className="flex justify-between items-center mb-1">
                    <div className="font-medium">{milestone.title}</div>
                    <span className="text-xs opacity-70">{milestone.date}</span>
                  </div>
                  <p className="text-xs opacity-70 mb-2">{milestone.description}</p>
                  <div className="flex justify-between items-center">
                    <div className="flex items-center">
                      <Gift className="h-3.5 w-3.5 text-purple-500 mr-1.5" />
                      <span className="text-xs text-purple-500">+{milestone.xp} XP</span>
                    </div>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${isDarkMode ? 'bg-yellow-500/20 text-yellow-400' : 'bg-yellow-100 text-yellow-600'}`}>
                      {milestone.type === 'social' ? 'Sosyal' : 
                       milestone.type === 'knowledge' ? 'Bilgi' : 'Sağlık'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </motion.div>
      )}
      
      {/* Social Statistics Tab */}
      {activeTab === 'social' && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0 }}
          className="space-y-6"
        >
          {/* Stats Cards */}
          <motion.div 
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="grid grid-cols-2 md:grid-cols-3 gap-4"
          >
            <StatCard 
              title="Toplam Bağlantılar" 
              value="321" 
              icon={Users} 
              description="+12% bu ay"
              color="#3b82f6"
              isDarkMode={isDarkMode}
              level={3}
              streak="14"
              nextMilestone="350 bağlantı"
              progress={92}
            />
            
            <StatCard 
              title="Mesaj Etkinliği" 
              value="1,458" 
              icon={MessageCircle} 
              description="85 okunmamış"
              color="#f59e0b"
              isDarkMode={isDarkMode}
              level={2}
              progress={78}
              reward="+25 XP (yeni mesaj)"
            />
            
            <StatCard 
              title="Sosyal Puan" 
              value="78" 
              icon={Medal} 
              description="Sosyal Kelebek 🦋"
              color="#10b981"
              isDarkMode={isDarkMode}
              progress={78}
              nextMilestone="80 puan = Yeni rozet"
            />
          </motion.div>
          
          {/* Social Growth Chart */}
          <motion.div 
            className={`p-4 rounded-xl ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-lg`}
            variants={itemVariants}
          >
            <h3 className="text-lg font-bold mb-4 flex items-center">
              <TrendingUp className="h-5 w-5 mr-2 text-blue-500" />
              Sosyal Ağ ve Kişisel Gelişim
            </h3>
            
            <div className="h-64 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={socialConnectionData}>
                  <XAxis 
                    dataKey="month" 
                    tick={{ fill: isDarkMode ? '#d1d5db' : '#4b5563' }}
                  />
                  <YAxis 
                    tick={{ fill: isDarkMode ? '#d1d5db' : '#4b5563' }}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: isDarkMode ? '#1f2937' : '#ffffff',
                      borderColor: isDarkMode ? '#374151' : '#e5e7eb',
                    }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="connections" 
                    name="Bağlantılar"
                    stroke="#3b82f6" 
                    strokeWidth={2}
                    dot={{ stroke: '#3b82f6', strokeWidth: 2, r: 4, fill: isDarkMode ? '#1f2937' : '#ffffff' }}
                    activeDot={{ r: 6, stroke: '#3b82f6', strokeWidth: 2, fill: '#60a5fa' }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="messages" 
                    name="Mesajlar"
                    stroke="#f59e0b" 
                    strokeWidth={2}
                    dot={{ stroke: '#f59e0b', strokeWidth: 2, r: 4, fill: isDarkMode ? '#1f2937' : '#ffffff' }}
                    activeDot={{ r: 6, stroke: '#f59e0b', strokeWidth: 2, fill: '#fbbf24' }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="personalGrowth" 
                    name="Kişisel Gelişim"
                    stroke="#10b981" 
                    strokeWidth={2}
                    dot={{ stroke: '#10b981', strokeWidth: 2, r: 4, fill: isDarkMode ? '#1f2937' : '#ffffff' }}
                    activeDot={{ r: 6, stroke: '#10b981', strokeWidth: 2, fill: '#34d399' }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="socialCapital" 
                    name="Sosyal Sermaye"
                    stroke="#8b5cf6" 
                    strokeWidth={2}
                    dot={{ stroke: '#8b5cf6', strokeWidth: 2, r: 4, fill: isDarkMode ? '#1f2937' : '#ffffff' }}
                    activeDot={{ r: 6, stroke: '#8b5cf6', strokeWidth: 2, fill: '#a78bfa' }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
            
            <div className="mt-3 grid grid-cols-2 gap-3">
              <div className={`p-2 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                <div className="flex justify-between items-center">
                  <span className="text-xs opacity-70">Ortalama sosyal etkileşim</span>
                  <span className="text-xs font-medium text-green-500">+18%</span>
                </div>
                <span className="text-lg font-medium">42.5</span>
                <span className="text-xs opacity-70"> /hafta</span>
              </div>
              
              <div className={`p-2 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                <div className="flex justify-between items-center">
                  <span className="text-xs opacity-70">Kişisel gelişim korelasyonu</span>
                  <span className="text-xs font-medium text-green-500">Yüksek</span>
                </div>
                <span className="text-lg font-medium">0.85</span>
                <span className="text-xs opacity-70"> /1.00</span>
              </div>
            </div>
          </motion.div>
          
          {/* Real Life vs Digital Balance */}
          <motion.div 
            className={`p-4 rounded-xl ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-lg`}
            variants={itemVariants}
          >
            <h3 className="text-lg font-bold mb-4 flex items-center">
              <Share2 className="h-5 w-5 mr-2 text-emerald-500" />
              Gerçek & Dijital Dünya Dengesi
            </h3>
            
            <div className="h-64 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={weeklyEngagementData}>
                  <XAxis 
                    dataKey="day" 
                    tick={{ fill: isDarkMode ? '#d1d5db' : '#4b5563' }}
                  />
                  <YAxis 
                    tick={{ fill: isDarkMode ? '#d1d5db' : '#4b5563' }}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: isDarkMode ? '#1f2937' : '#ffffff',
                      borderColor: isDarkMode ? '#374151' : '#e5e7eb',
                    }}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="realWorldValue" 
                    name="Gerçek Hayat Etkinliği"
                    stroke="#10b981" 
                    fill={isDarkMode ? "rgba(16, 185, 129, 0.2)" : "rgba(16, 185, 129, 0.1)"}
                    strokeWidth={2}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="digitalValue" 
                    name="Dijital Aktivite"
                    stroke="#ec4899" 
                    fill={isDarkMode ? "rgba(236, 72, 153, 0.2)" : "rgba(236, 72, 153, 0.1)"}
                    strokeWidth={2}
                  />
                  <Line
                    type="monotone"
                    dataKey="xpGained"
                    name="Kazanılan XP"
                    stroke="#f59e0b"
                    strokeWidth={2}
                    dot={{ stroke: '#f59e0b', strokeWidth: 2, r: 4, fill: isDarkMode ? '#1f2937' : '#ffffff' }}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
            
            <div className="mt-2 flex items-center justify-center">
              <div className={`px-3 py-1.5 rounded-lg ${isDarkMode ? 'bg-green-500/20' : 'bg-green-100'} flex items-center space-x-2`}>
                <div className="w-3 h-3 rounded-full bg-green-500"></div>
                <span className="text-xs font-medium text-green-500">75% Gerçek hayat odaklı - Optimal denge!</span>
              </div>
            </div>
          </motion.div>
          
          {/* Top Connections */}
          <motion.div 
            className={`p-4 rounded-xl ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-lg`}
            variants={itemVariants}
          >
            <h3 className="text-lg font-bold mb-4 flex items-center">
              <UserPlus className="h-5 w-5 mr-2 text-purple-500" />
              En Sık Etkileşim Kurulan Kişiler
            </h3>
            
            <div className="space-y-3">
              {[
                { name: 'Ahmet Yılmaz', interactions: 127, lastActive: '1 saat önce', relationshipScore: 85, relationshipLevel: 'Dost', topicMatch: ['Teknoloji', 'Sağlık'] },
                { name: 'Mehmet Can', interactions: 89, lastActive: '3 saat önce', relationshipScore: 72, relationshipLevel: 'Yakın Arkadaş', topicMatch: ['Spor', 'İş'] },
                { name: 'Ayşe Demir', interactions: 65, lastActive: 'Dün', relationshipScore: 63, relationshipLevel: 'Arkadaş', topicMatch: ['Kültür', 'Sanat'] }
              ].map((contact, index) => (
                <div 
                  key={index} 
                  className={`p-3 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}
                >
                  <div className="flex justify-between items-center">
                    <span className="font-medium">{contact.name}</span>
                    <span className="text-xs opacity-70">{contact.lastActive}</span>
                  </div>
                  <div className="mt-1 flex justify-between items-center">
                    <div className="flex items-center">
                      <Activity className="h-3.5 w-3.5 text-blue-500 mr-1" />
                      <span className="text-xs">{contact.interactions} etkileşim</span>
                    </div>
                    <div className="flex items-center">
                      <HeartHandshake className="h-3.5 w-3.5 text-red-500 mr-1" />
                      <span className="text-xs">{contact.relationshipScore}% uyum</span>
                    </div>
                  </div>
                  <div className="mt-2 flex justify-between items-center">
                    <div className="flex items-center">
                      <div className={`px-2 py-0.5 rounded-full text-xs ${isDarkMode ? 'bg-purple-500/20 text-purple-400' : 'bg-purple-100 text-purple-600'}`}>
                        {contact.relationshipLevel}
                      </div>
                    </div>
                    <div className="flex space-x-1">
                      {contact.topicMatch.map((topic, i) => (
                        <div key={i} className={`px-1.5 py-0.5 rounded-full text-[10px] ${isDarkMode ? 'bg-blue-500/20 text-blue-400' : 'bg-blue-100 text-blue-600'}`}>
                          {topic}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            <motion.button
              className={`mt-4 w-full py-2 rounded-lg ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-100 hover:bg-gray-200'} text-sm`}
              whileHover={{ y: -2 }}
              whileTap={{ scale: 0.98 }}
            >
              Tüm Bağlantıları Göster
            </motion.button>
          </motion.div>
        </motion.div>
      )}
      
      {/* Achievements Tab */}
      {activeTab === 'achievements' && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0 }}
          className="space-y-6"
        >
          {/* Achievement Stats */}
          <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-lg`}>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold flex items-center">
                <Trophy className="h-5 w-5 mr-2 text-yellow-500" />
                Toplam Başarımlar
              </h3>
              <div className={`px-3 py-1 rounded-full ${isDarkMode ? 'bg-yellow-500/20' : 'bg-yellow-100'} text-sm flex items-center`}>
                <span className="text-yellow-500 font-medium">42 / 75</span>
              </div>
            </div>
            
            <div className="mb-3">
              <div className="flex justify-between text-xs mb-1">
                <span>İlerleme</span>
                <span>56%</span>
              </div>
              <div className="w-full h-2 bg-gray-700 rounded-full overflow-hidden">
                <motion.div
                  className="h-full bg-green-500"
                  initial={{ width: 0 }}
                  animate={{ width: '56%' }}
                  transition={{ duration: 1, ease: "easeOut" }}
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-2">
              <div className="text-xs flex items-center">
                <div className="w-3 h-3 rounded-full bg-green-500 mr-1.5"></div>
                <span className="opacity-70">Tamamlanan: 42</span>
              </div>
              <div className="text-xs flex items-center">
                <div className="w-3 h-3 rounded-full bg-yellow-500 mr-1.5"></div>
                <span className="opacity-70">İlerlenen: 15</span>
              </div>
              <div className="text-xs flex items-center">
                <div className="w-3 h-3 rounded-full bg-red-500 mr-1.5"></div>
                <span className="opacity-70">Kilitli: 18</span>
              </div>
              <div className="text-xs flex items-center">
                <div className="w-3 h-3 rounded-full bg-purple-500 mr-1.5"></div>
                <span className="opacity-70">Nadir: 6/12</span>
              </div>
            </div>
          </div>
            {/* Achievement Categories Distribution */}
          <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-lg`}>
            <h3 className="text-lg font-bold mb-4 flex items-center">
              <PieChartIcon className="h-5 w-5 mr-2 text-blue-500" />
              Başarım Kategorileri
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">              <div className="h-60">
                <ResponsiveContainer width="100%" height="100%">
                  <ReChartPieChart>
                    <Pie
                      data={achievementCategories}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {achievementCategories.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(value, name, props) => [`${value} başarım`, props.payload.name]}
                      contentStyle={{
                        backgroundColor: isDarkMode ? '#1f2937' : '#ffffff',
                        borderColor: isDarkMode ? '#374151' : '#e5e7eb',
                      }}
                    />
                  </ReChartPieChart>
                </ResponsiveContainer>
              </div>
              
              <div className="flex flex-col justify-center space-y-3">
                {achievementCategories.map((category, index) => (
                  <div key={index} className="flex items-center">
                    <div 
                      className="w-3 h-3 rounded-full mr-2" 
                      style={{ backgroundColor: category.color }}
                    />
                    <div className="flex-1">
                      <div className="flex justify-between items-center mb-1">
                        <span className="text-sm">{category.name}</span>
                        <span className="text-xs font-medium">{category.value}</span>
                      </div>
                      <div className="w-full h-1.5 bg-gray-700 rounded-full overflow-hidden">
                        <div 
                          className="h-full" 
                          style={{ 
                            width: `${(category.value / achievementCategories.reduce((sum, cat) => sum + cat.value, 0)) * 100}%`,
                            backgroundColor: category.color
                          }}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          
          {/* Badges Collection */}
          <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-lg`}>
            <h3 className="text-lg font-bold mb-4 flex items-center">
              <Award className="h-5 w-5 mr-2 text-amber-500" />
              Son Kazanılan Rozetler
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <BadgeCard 
                title="Sosyal Kelebek"
                icon={UserPlus}
                description="Bir günde 5+ kişiyle anlamlı etkileşim kur"
                unlocked={true}
                color="blue"
                isDarkMode={isDarkMode}
              />
              
              <BadgeCard 
                title="Kitap Kurdu"
                icon={BookOpen}
                description="Bir ayda 4 kitap oku ve not al"
                unlocked={true}
                color="purple"
                isDarkMode={isDarkMode}
              />
              
              <BadgeCard 
                title="Erken Kalkan"
                icon={Calendar}
                description="7 gün üst üste sabah 6'da kalk"
                unlocked={true}
                color="amber"
                isDarkMode={isDarkMode}
              />
              
              <BadgeCard 
                title="Dünya Vatandaşı"
                icon={Globe}
                description="5 farklı ülkeye seyahat et"
                unlocked={false}
                progress={60}
                color="blue"
                isDarkMode={isDarkMode}
              />
            </div>
            
            <motion.button
              className={`mt-4 w-full py-2 rounded-lg ${isDarkMode ? 'bg-amber-500/20 text-amber-400 hover:bg-amber-500/30' : 'bg-amber-100 text-amber-600 hover:bg-amber-200'} text-sm`}
              whileHover={{ y: -2 }}
              whileTap={{ scale: 0.98 }}
            >
              Tüm Rozetleri Göster (42)
            </motion.button>
          </div>
          
          {/* Next Achievements to Target */}
          <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-lg`}>
            <h3 className="text-lg font-bold mb-4 flex items-center">
              <Target className="h-5 w-5 mr-2 text-red-500" />
              Hedeflenecek Başarımlar
            </h3>
            
            <div className="space-y-3">
              {[
                { name: "Sosyal Öncü", description: "50 kişiyle tanış ve bilgilerini kaydet", progress: 85, target: "42/50 kişi", xp: 250, type: "social" },
                { name: "Sağlık Uzmanı", description: "30 gün düzenli egzersiz yap", progress: 60, target: "18/30 gün", xp: 300, type: "health" },
                { name: "Bilgi Koleksiyoncusu", description: "25 kitap oku ve özet çıkar", progress: 40, target: "10/25 kitap", xp: 350, type: "knowledge" }
              ].map((achievement, index) => (
                <div 
                  key={index}
                  className={`p-3 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'} border-l-4 ${
                    achievement.type === 'social' ? 'border-blue-500' :
                    achievement.type === 'health' ? 'border-green-500' : 'border-purple-500'
                  }`}
                >
                  <div className="flex justify-between items-center mb-1">
                    <span className="font-medium">{achievement.name}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      achievement.type === 'social' ? (isDarkMode ? 'bg-blue-500/20 text-blue-400' : 'bg-blue-100 text-blue-600') :
                      achievement.type === 'health' ? (isDarkMode ? 'bg-green-500/20 text-green-400' : 'bg-green-100 text-green-600') :
                      (isDarkMode ? 'bg-purple-500/20 text-purple-400' : 'bg-purple-100 text-purple-600')
                    }`}>
                      {achievement.target}
                    </span>
                  </div>
                  
                  <p className="text-xs opacity-70 mb-2">{achievement.description}</p>
                  
                  <div className="w-full h-1.5 bg-gray-600 rounded-full overflow-hidden mb-2">
                    <div 
                      className={`h-full ${
                        achievement.type === 'social' ? 'bg-blue-500' :
                        achievement.type === 'health' ? 'bg-green-500' : 'bg-purple-500'
                      }`} 
                      style={{ width: `${achievement.progress}%` }}
                    />
                  </div>
                  
                  <div className="flex justify-between items-center">
                    <div className="flex items-center">
                      <Gift className="h-3.5 w-3.5 text-yellow-500 mr-1" />
                      <span className="text-xs text-yellow-500">+{achievement.xp} XP</span>
                    </div>
                    <span className="text-xs opacity-70">{achievement.progress}% tamamlandı</span>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="mt-4 flex justify-center">
              <div className={`inline-block px-3 py-2 rounded-lg ${isDarkMode ? 'bg-blue-500/20' : 'bg-blue-100'}`}>
                <span className="text-sm text-blue-500">AI analizi: Bu ay 3 başarım hedefine ulaşabilirsiniz! 🚀</span>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default SocialStatsComponent;
